import React, { useState, useEffect, useRef } from 'react';
import { PlusIcon } from '@heroicons/react/24/outline';

/**
 * BlockInsertZone Component
 * 
 * Renders an invisible hover zone between blocks that shows a plus icon
 * after 0.7 seconds of hover, allowing users to insert a new block
 */
const BlockInsertZone = ({
  afterBlockId,
  onInsert,
  readOnly = false,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [showPlus, setShowPlus] = useState(false);
  const timerRef = useRef(null);

  // Handle mouse enter - start timer
  const handleMouseEnter = () => {
    if (readOnly) return;
    
    setIsHovered(true);
    
    // Clear any existing timer
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    
    // Set timer for 0.7 seconds
    timerRef.current = setTimeout(() => {
      setShowPlus(true);
    }, 700);
  };

  // Handle mouse leave - reset state
  const handleMouseLeave = () => {
    setIsHovered(false);
    setShowPlus(false);
    
    // Clear timer if it exists
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  // Handle click on plus icon
  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (readOnly || !onInsert) return;
    
    onInsert(afterBlockId);
    
    // Reset state after click
    setIsHovered(false);
    setShowPlus(false);
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  // Don't render in read-only mode
  if (readOnly) {
    return null;
  }

  return (
    <div
      className="relative min-h-[8px] py-2 transition-all duration-200 hover:min-h-[32px] group"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{
        scrollMargin: 0,
        scrollPadding: 0,
      }}
    >
      {/* Plus icon - appears after 1.5s hover */}
      {showPlus && (
        <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10">
          <button
            onClick={handleClick}
            className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-500 hover:bg-blue-600 text-white shadow-lg transition-all duration-200 animate-fade-in"
            title="Insert new block"
            aria-label="Insert new block"
          >
            <PlusIcon className="h-5 w-5" />
          </button>
        </div>
      )}
    </div>
  );
};

export default BlockInsertZone;

